import React, { useEffect, useState, useCallback, Suspense } from 'react';
import { connect } from 'react-redux';
import {
  getRegions,
  deleteRegion,
  getAllActiveStores,
} from 'store/requests/definitions';
import 'antd/dist/antd.css';
import { Modal, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import trTR from 'antd/es/locale/tr_TR';
import { CSVLink } from 'react-csv';
import { ConfigProvider } from 'antd';
import { debouncedDataLoadWaitMs, ORDER_TYPES } from 'helpers/constants';
import { Tooltip, Row, Button, Table, Col, Form, Tag, Dropdown } from 'antd';
import {
  ExclamationCircleOutlined,
  FilterOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  PlusOutlined,
  DownloadOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { PAGE_URLS } from 'helpers/endpoints';
const Add = React.lazy(() => import('./Add'));
const Edit = React.lazy(() => import('./Edit'));
const RegionsTableFilters = React.lazy(() => import('./RegionsTableFilters'));
let lastQuery = false;

const RegionsView = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const { confirm } = Modal;
  const [filters, setFilters] = useState({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [activeStores, setActiveStores] = useState([]);
  const [activeStoresLoading, setActiveStoresLoading] = useState(false);
  const [selectedEditRegion, setSelectedEditRegion] = useState(null);

  const [tableParams, setTableParams] = useState({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `Toplam ${total} kayıt`,
      pageSizeOptions: [20, 50, 100],
      size: 'small',
      current: 1,
      pageSize: 20,
    },
  });

  useEffect(() => {
    fetchData();
    setIsFiltersOpen(false);
    return () => {
      lastQuery = null;
      setLoading(false);
    };
  }, [JSON.stringify(tableParams), JSON.stringify(filters)]);

  useEffect(() => {
    (async () => {
      setActiveStoresLoading(true);
      const allActiveStores = await getAllActiveStores();
      setActiveStores(allActiveStores);
      setActiveStoresLoading(false);
    })();
  }, []);

  const fetchData = () => {
    const queryString = getQueryString();
    loadData(queryString);
  };

  const columns = [
    {
      title: t('regionCode'),
      dataIndex: 'code',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('regionName'),
      dataIndex: 'name',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('regionalManager'),
      dataIndex: 'user',
      sorter: true,
      showSorterTooltip: false,
      render: (item) =>
        item ? (
          <p>{`${item.nameSurname} ( ${item.phoneNumber} - ${item?.email ? item.email : '--'} )`}</p>
        ) : (
          '--'
        ),
    },
    {
      title: t('stores'),
      dataIndex: 'allStores',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      width: 50,
      render: (dataItem) =>
        isCrudEnabled && (
          <Dropdown
            menu={{
              items: [
                {
                  label: t('edit'),
                  icon: activeStoresLoading ? (
                    <LoadingOutlined />
                  ) : (
                    <EditOutlined />
                  ),
                  onClick: () => {
                    setSelectedEditRegion(dataItem);
                    setIsEditModalOpen(true);
                  },
                  disabled: activeStoresLoading,
                },
                {
                  danger: true,
                  label: t('delete'),
                  icon: <DeleteOutlined />,
                  onClick: () => {
                    showDeleteConfirm(selectedItemId);
                  },
                },
              ],
            }}
            placement="bottomRight"
            arrow
            trigger={['click']}
            onClick={() => setSelectedItemId(dataItem.id)}
          >
            <Button shape="circle" icon={<MoreOutlined />}></Button>
          </Dropdown>
        ),
    },
  ];

  const actions = [
    {
      isCrudEnabled,
      type: 'primary',
      title: t('add'),
      icon: activeStoresLoading ? <LoadingOutlined /> : <PlusOutlined />,
      onClick: () => setIsAddModalOpen(true),
      disabled: activeStoresLoading,
    },
    {
      isCrudEnabled: true,
      type: '',
      title: t('download'),
      customContent: (
        <CSVLink
          filename={'Regions'}
          data={[
            columns.map((item) => item.title),
            ...rows.map((item) => [
              item.code,
              item.name,
              item.user?.nameSurname,
              item.allStores,
            ]),
          ]}
        >
          <Button
            style={{ marginRight: '3px' }}
            shape="round"
            icon={<DownloadOutlined />}
          ></Button>
        </CSVLink>
      ),
      onClick: () => {},
      disabled: false,
    },
  ];

  const renderActionButtons = () => {
    return actions.map((item, idx) => {
      return (
        <Tooltip title={item.title} key={idx}>
          {item.isCrudEnabled &&
            (item.customContent ?? (
              <Button
                type={item?.type}
                shape="round"
                icon={item?.icon}
                onClick={item.onClick}
                disabled={item.disabled}
                style={{ marginBottom: '10px', marginRight: '3px' }}
                danger={item?.danger}
              />
            ))}
        </Tooltip>
      );
    });
  };

  function showDeleteConfirm(itemId) {
    confirm({
      title: t('modalDeleteTitle'),
      icon: <ExclamationCircleOutlined />,
      content: t('regionDelete'),
      okText: t('delete'),
      okType: 'danger',
      cancelText: t('cancel'),
      centered: true,
      onOk() {
        deleteRegion(itemId).then((res) => {
          if (res.isSuccess) {
            notificationDelete('success');
            lastQuery = null;
            const queryString = getQueryString();
            loadData(queryString);
          } else {
            notification.error({ message: res.message });
          }
        });
      },
      onCancel() {},
    });
  }

  const renderSelectedFilters = () => {
    const fieldLocalizationVars = {
      code: 'regionCode',
      id: 'regionId',
      'user.nameSurname': 'regionalManager',
      'stores.id': 'storeId',
    };
    return Object.keys(filters).map((item, idx) => {
      if (filters[item]) {
        return (
          <Tag
            key={idx}
            closable
            color="geekblue"
            onClose={(e) => {
              e.preventDefault();
              removeItemFromFilters(item);
            }}
          >
            {`${t(fieldLocalizationVars[item])} : ${filters[item]}`}
          </Tag>
        );
      }
    });
  };

  const notificationDelete = (type) => {
    notification[type]({
      message: t('processCompleted'),
      description: t('regionDeleted'),
    });
  };

  const removeItemFromFilters = (destinationItem) => {
    Object.keys(filters).forEach((item) => {
      if (item === destinationItem) {
        const editedFilters = {
          ...filters,
          [destinationItem]: undefined,
        };
        setFilters(editedFilters);
        form.setFieldsValue(editedFilters);
      }
    });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      ...sorter,
    });
  };

  const getQueryString = () => {
    let queryString = 'filter=active==true ';

    if (Object.values(filters).some((item) => !!item)) {
      Object.keys(filters).forEach((item, idx) => {
        if (filters[item]) {
          queryString += ` and ${encodeURI(item)}=='${encodeURI(filters[item])}'`;
        }
      });
    }
    queryString = `${queryString}&page=${tableParams.pagination.current - 1}&size=${tableParams.pagination.pageSize}`;
    if (tableParams.column) {
      queryString = `${queryString}&sort=${tableParams.column.dataIndex},${ORDER_TYPES[tableParams.order]}`;
    }
    return queryString;
  };

  async function loadDataWithDebounce(queryString) {
    if (queryString !== lastQuery && !loading) {
      lastQuery = queryString;
      setLoading(true);
      getRegions(queryString)
        .then((region) => {
          const { _embedded, page } = region;
          const { regionResponseDtoList } = _embedded ? _embedded : [];
          let allStores = '';
          regionResponseDtoList?.forEach((element) => {
            for (let i = 0; i < element.stores.length; i++) {
              if (i === 0) {
                allStores =
                  '( ' +
                  element.stores[i]?.code +
                  ' ) ' +
                  element.stores[i].name;
              } else {
                allStores =
                  allStores +
                  ' - ' +
                  ' ( ' +
                  element.stores[i]?.code +
                  ' ) ' +
                  element.stores[i]?.name;
              }
            }
            element.allStores = allStores;
            allStores = [];
          });
          setRows(regionResponseDtoList ? regionResponseDtoList : []);
          setTableParams({
            ...tableParams,
            pagination: {
              ...tableParams.pagination,
              total: page.totalElements,
            },
          });
        })
        .finally(() => setLoading(false));
    }
  }

  const loadData = useCallback(
    debounce((queryString) => {
      loadDataWithDebounce(queryString);
    }, debouncedDataLoadWaitMs),
  );

  return (
    <div style={{ position: 'relative' }}>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <Col>{renderActionButtons()}</Col>
        <Col>
          <Button
            shape="round"
            icon={<FilterOutlined />}
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          >
            {t('filters')}
          </Button>
        </Col>
      </Row>
      <Row style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {renderSelectedFilters()}
      </Row>
      <Row style={{ marginTop: '10px' }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={tableParams.pagination}
            loading={loading}
            onChange={handleTableChange}
            rowKey={(record) => record.id}
            scroll={{
              y: '70vh',
            }}
          />
        </ConfigProvider>
      </Row>
      <Suspense fallback={<></>}>
        {isFiltersOpen && (
          <RegionsTableFilters
            setIsFiltersOpen={setIsFiltersOpen}
            setFilters={setFilters}
            form={form}
          />
        )}
        {isAddModalOpen && (
          <Add
            activeStores={activeStores}
            onCloseHandler={() => setIsAddModalOpen(false)}
            onSuccessCallback={fetchData}
          />
        )}
        {isEditModalOpen && (
          <Edit
            selectedEditRegion={selectedEditRegion}
            activeStores={activeStores}
            onCloseHandler={() => setIsEditModalOpen(false)}
            onSuccessCallback={fetchData}
          />
        )}
      </Suspense>
    </div>
  );
};

export default connect()(RegionsView);
